import { Helmet } from "react-helmet";
import Header from "../components/Header";
import Footer from "../components/Footer";
import MedhaAISection from "../components/MedhaAISection";
import MedhaTeachers from "../components/MedhaTeachers";
import AppSuiteSection from "../components/AppSuiteSection";
import React, { useRef } from "react";
import { motion } from "framer-motion";

// ── Service images (real photos) ─────────────────────────────────────────────
import imgCogradSchool from "../assets/key-offerings/school1.webp";
import imgAISchool     from "../assets/key-offerings/ai school.jpg";
import imgMedhaAI      from "../assets/innovation/smart-classes.webp";
import imgDreamLabs    from "../assets/Our-projects/vr for school .webp";
import imgDigitalising from "../assets/key-offerings/school2.webp";
import imgCapacity     from "../assets/key-offerings/upskill1.webp";
import imgContent      from "../assets/key-offerings/upskill2.webp";
import imgIncubation   from "../assets/key-offerings/sip-1.jpg";
import imgNextGen      from "../assets/nextgen/ng-hero.jpg";


import "./Services.scss";
import { impact } from "../data/stats";
import AsOfNote from "../components/AsOfNote";
import {
  ArrowUpRight,
  ChevronLeft,
  ChevronRight,
  GraduationCap,
  Bot,
  Monitor,
  BookOpen,
  Target,
  Cpu,
  BarChart2,
  Trophy,
  Lightbulb,
  FlaskConical,
  Rocket,
} from "lucide-react";

// ── Data ─────────────────────────────────────────────────────────────────────
const CORE_SERVICES = [
  {
    id: "01", icon: GraduationCap, accent: "#0041e8",
    name: "Cograd School",
    tagline: "End-to-end school transformation",
    desc: "NEP-aligned curriculum, AI integration, teacher training, and smart infrastructure — complete transformation for schools across rural and urban India.",
    chips: ["NEP 2020 Aligned", "AI Integration", "Holistic Curriculum"],
    image: imgCogradSchool, href: "/contact-us",
  },
  {
    id: "02", icon: Cpu, accent: "#0891b2",
    name: "AI School",
    tagline: "Future-ready AI-first campus",
    desc: "Purpose-built AI Labs with hands-on AI, robotics, and coding zones — transforming schools into innovation-driven, AI-first campuses.",
    chips: ["AI Labs", "Robotics", "Innovation Zone"],
    image: imgAISchool, href: "/contact-us",
  },
  {
    id: "03", icon: Rocket, accent: "#0041e8",
    name: "NextGen School",
    tagline: "A future-ready learning model",
    desc: "A complete NextGen learning model blending AI, hands-on labs, project-based learning, and 21st-century skills to prepare students for tomorrow.",
    chips: ["Project-Based", "21st-Century Skills", "Hands-on Labs"],
    image: imgNextGen, href: "/nextgen-school",
  },
  {
    id: "04", icon: Bot, accent: "#7c3aed",
    name: "Medha AI Smart Classes",
    tagline: "AI-powered classroom experience",
    desc: "Personalized learning, automated lesson planning, and real-time student insights — Medha AI puts the power of artificial intelligence in every classroom.",
    chips: ["AI Personalization", "Smart Boards", "Live Analytics"],
    image: imgMedhaAI, href: "https://medha.cograd.in/",
  },
  {
    id: "05", icon: FlaskConical, accent: "#059669",
    name: "Dream Labs",
    tagline: "Next-gen technology labs",
    desc: "Fully equipped AI, robotics, VR, and coding labs that foster hands-on innovation and prepare students for careers of tomorrow.",
    chips: ["AI & Robotics", "VR Labs", "10+ Courses"],
    image: imgDreamLabs, href: "/contact-us",
  },
  {
    id: "06", icon: Monitor, accent: "#d97706",
    name: "Digitalising Schools",
    tagline: "Complete digital transformation",
    desc: "AI-powered LMS and ERP software that modernizes admissions, attendance, fee collection, analytics, and every school operation.",
    chips: ["AI-powered LMS", "School ERP", "Admin Automation"],
    image: imgDigitalising, href: "/contact-us",
  },
  {
    id: "07", icon: Target, accent: "#e11d48",
    name: "Capacity Building",
    tagline: "Teacher upskilling & training",
    desc: "Structured teacher training aligned with NCF 2023 and NEP 2020 — covering AI tools, innovative pedagogy, and emerging technologies.",
    chips: ["NCF 2023", "NEP 2020", "200+ Workshops"],
    image: imgCapacity, href: "/contact-us",
  },
  {
    id: "08", icon: BookOpen, accent: "#0891b2",
    name: "Content & Curriculum",
    tagline: "Future-ready learning materials",
    desc: "Custom curricula integrating 21st-century skills, critical thinking, and emerging tech — fully aligned with NCF and NEP guidelines.",
    chips: ["Custom Curriculum", "10+ Tech Courses", "Bilingual"],
    image: imgContent, href: "/contact-us",
  },
  {
    id: "09", icon: Lightbulb, accent: "#8b5cf6",
    name: "School Incubation",
    tagline: "Startup ecosystem for school innovators",
    desc: "Cograd's Incubation Program nurtures student entrepreneurs and school leaders with mentorship, funding support, and resources to build the next generation of education startups.",
    chips: ["Mentorship", "Funding Support", "EdTech Startups"],
    image: imgIncubation, href: "/incubation",
  },
];

const CTA_FEATS = [
  { icon: Target,    label: "NEP-Aligned"    },
  { icon: Cpu,       label: "AI-Powered"     },
  { icon: BarChart2, label: "Live Analytics" },
  { icon: Trophy,    label: "Proven Results" },
];

const up = (i = 0) => ({
  initial:     { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0  },
  viewport:    { once: true, margin: "-50px" },
  transition:  { duration: 0.52, delay: i * 0.07, ease: [0.22, 1, 0.36, 1] },
});

// Cards inside overflow-x scroll container — use animate (not whileInView)
// because IntersectionObserver doesn't fire reliably inside clipped containers
const cardAnim = (i = 0) => ({
  initial:    { opacity: 0, y: 20 },
  animate:    { opacity: 1, y: 0  },
  transition: { duration: 0.5, delay: 0.15 + i * 0.07, ease: [0.22, 1, 0.36, 1] },
});

// ── Page ─────────────────────────────────────────────────────────────────────
export default function Services() {
  const scrollRef = useRef(null);
  const scroll = (dir) => {
    scrollRef.current?.scrollBy({ left: dir * 336, behavior: 'smooth' });
  };

  return (
    <div className="svc-page">
      <Helmet>
        <title>Our Services - Cograd Education Solutions</title>
        <meta
          name="description"
          content="Cograd's complete suite: Medha AI Smart Classes, Dream Labs, School Digitalization, Capacity Building, App Suite, School Incubation and more."
        />
      </Helmet>

      <Header />

      {/* ── HERO — text-only centered ── */}
      <section className="svc-hero">
        <div className="svc-hero__inner">
          <motion.div
            initial="hidden"
            animate="show"
            variants={{ show: { transition: { staggerChildren: 0.09 } } }}
          >
            <motion.div
              className="svc-hero__creds"
              variants={{ hidden: { opacity: 0, y: 14 }, show: { opacity: 1, y: 0, transition: { duration: 0.42 } } }}
            >
              <div className="svc-hero__badge">
                <span className="svc-hero__badge-dot" />
                NEP 2020 &amp; NCF 2023 Aligned
              </div>
              <div className="svc-hero__badge">
                <span className="svc-hero__badge-dot svc-hero__badge-dot--blue" />
                {impact.schools.display} {impact.schools.label}
              </div>
              <AsOfNote className="svc-hero__asof" />
            </motion.div>

            <motion.h1
              className="svc-hero__h1"
              variants={{ hidden: { opacity: 0, y: 20 }, show: { opacity: 1, y: 0, transition: { duration: 0.5 } } }}
            >
              Complete Education{" "}
              <span className="svc-hero__grad">Transformation</span>
              <br />for Every School
            </motion.h1>

            <motion.p
              className="svc-hero__sub"
              variants={{ hidden: { opacity: 0, y: 14 }, show: { opacity: 1, y: 0, transition: { duration: 0.42 } } }}
            >
              From AI-powered smart classes to full school digitalization —
              Cograd delivers end-to-end solutions that make every school
              future-ready across rural and urban India.
            </motion.p>

            <motion.div
              className="svc-hero__actions"
              variants={{ hidden: { opacity: 0, y: 14 }, show: { opacity: 1, y: 0, transition: { duration: 0.42 } } }}
            >
              <button
                className="svc-hero__btn-solid"
                onClick={() => (window.location.href = "/contact-us")}
              >
                Partner With Us <ArrowUpRight size={15} />
              </button>
              <a className="svc-hero__btn-outline" href="#svc-core">
                Explore Services <ArrowUpRight size={14} />
              </a>
            </motion.div>



          </motion.div>

        </div>
      </section>

      {/* ── CORE SERVICES ── */}
      <div className="svc-core-bg">
      <section className="svc-core" id="svc-core">
        <div className="svc-core__head">
          <motion.div className="svc-core__pill" {...up(0)}>
            <span className="svc-core__pill-dot" />
            What We Offer
          </motion.div>
          <motion.h2 className="svc-core__h2" {...up(0.07)}>
            Ways We Elevate Education
          </motion.h2>
          <motion.p className="svc-core__sub" {...up(0.12)}>
            Every service works independently or as part of a complete transformation package.
          </motion.p>
        </div>

        <div className="svc-core__track-wrap">
          <button
            className="svc-core__arrow svc-core__arrow--left"
            onClick={() => scroll(-1)}
            aria-label="Scroll left"
          >
            <ChevronLeft size={18} />
          </button>

          <div className="svc-grid" ref={scrollRef}>
            {CORE_SERVICES.map((s, i) => {
              const Icon = s.icon;
              return (
                <motion.div key={i} className="svc-card" {...cardAnim(i)}>
                  <div className="svc-card__img">
                    <img src={s.image} alt={s.name} loading="lazy" />
                    <span className="svc-card__icon-dot">
                      <Icon size={14} color="#fff" />
                    </span>
                  </div>
                  <div className="svc-card__body">
                    <h3 className="svc-card__name">{s.name}</h3>
                    <p className="svc-card__tagline">{s.tagline}</p>
                    <p className="svc-card__desc">{s.desc}</p>
                    <div className="svc-card__chips">
                      {s.chips.map((c, j) => (
                        <span key={j} className="svc-card__chip">{c}</span>
                      ))}
                    </div>
                    <a
                      href={s.href}
                      className="svc-card__cta"
                      {...(s.href.startsWith("http") ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                    >
                      Get Started <ArrowUpRight size={13} />
                    </a>
                  </div>
                </motion.div>
              );
            })}
          </div>

          <button
            className="svc-core__arrow svc-core__arrow--right"
            onClick={() => scroll(1)}
            aria-label="Scroll right"
          >
            <ChevronRight size={18} />
          </button>
        </div>
      </section>
      </div>

      {/* ── MEDHA AI ── */}
      <div className="svc-medha-wrap">
        <div className="svc-section-hd">
          <motion.span className="svc-pill" {...up(0)}>Medha AI Platform</motion.span>
          <motion.h2 className="svc-section-hd__h2" {...up(0.07)}>
            AI Built for India's Classrooms
          </motion.h2>
          <motion.p className="svc-section-hd__sub" {...up(0.12)}>
            Lesson plans, quizzes, assessments, 3D explorer — all in one AI-powered platform.
          </motion.p>
        </div>
        <MedhaAISection />
        <MedhaTeachers />
      </div>

      {/* ── APP SUITE ── */}
      <div className="svc-suite-wrap">
        <div className="svc-section-hd">
          <motion.span className="svc-pill" {...up(0)}>Digital Ecosystem</motion.span>
          <motion.h2 className="svc-section-hd__h2" {...up(0.07)}>
            One App Suite. Every Stakeholder.
          </motion.h2>
          <motion.p className="svc-section-hd__sub" {...up(0.12)}>
            Purpose-built tools for schools, teachers, and parents — all connected in one ecosystem.
          </motion.p>
        </div>
        <AppSuiteSection />
      </div>

{/* ── CTA — matches Home page theme ── */}
      <section className="svc-cta">
        <motion.div
          className="svc-cta__card"
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        >
          <h2 className="svc-cta__h2">
            Ready to Upgrade<br />Your School?
          </h2>
          <p className="svc-cta__sub">
            Innovative solutions to elevate your institution with Cograd —
            cutting-edge technology, personalized support, and tailored programs built for India.
          </p>
          <div className="svc-cta__feats">
            {CTA_FEATS.map(({ icon: Icon, label }, i) => (
              <span key={i} className="svc-cta__feat">
                <Icon size={12} strokeWidth={2.5} /> {label}
              </span>
            ))}
          </div>
          <button
            className="svc-cta__btn"
            onClick={() => (window.location.href = "/contact-us")}
          >
            Upgrade Now <ArrowUpRight size={16} />
          </button>
        </motion.div>
      </section>

      <Footer />
    </div>
  );
}
